import { motion } from "framer-motion";
import { useState, useRef } from "react";

interface SubmitMessage {
  type: "success" | "error";
  message: string;
}

const roleOptions = [
  { value: "", label: "Wähle deine Rolle" },
  { value: "beleuchter", label: "Beleuchter" },
  { value: "kameramann", label: "Kameramann / Kamerafrau" },
  { value: "3d-artist", label: "3D Artist" },
  { value: "art-director", label: "Art Director" },
  { value: "modellbauer", label: "Modellbauer" },
  { value: "sfx-makeup", label: "SFX-Make-up Artist" },
  { value: "compositing", label: "Compositing-Wizard" },
  { value: "colorist", label: "Colorist" },
  { value: "cutter", label: "Cutter / Editor" },
  { value: "ki-prompter", label: "KI-Prompter" },
  { value: "other", label: "Anderes" },
];

const visualRoles = [
  "Beleuchter",
  "Kameramann",
  "3D Artist",
  "Art Director",
  "Modellbauer",
  "SFX-Make-up",
  "Compositing",
  "Colorist",
  "Cutter",
  "KI-Prompter",
];

interface JobApplicationFormData {
  name: string;
  email: string;
  role: string;
  showreelUrl: string;
  message: string;
  pdfs: File[];
}

function validateJobApplication(data: JobApplicationFormData): string[] {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length < 2) {
    errors.push("Name muss mindestens 2 Zeichen lang sein");
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!data.email || !emailRegex.test(data.email)) {
    errors.push("Bitte gib eine gültige E-Mail-Adresse ein");
  }

  if (!data.role) {
    errors.push("Bitte wähle deine Rolle aus");
  }

  // Showreel URL is now optional, but validate format if provided
  if (data.showreelUrl && data.showreelUrl.trim().length > 0) {
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (!urlRegex.test(data.showreelUrl)) {
      errors.push("Bitte gib eine gültige URL zu deinem Showreel ein");
    }
  }

  // PDF validation
  if (data.pdfs && data.pdfs.length > 0) {
    if (data.pdfs.length > 3) {
      errors.push("Maximal 3 PDF-Dateien erlaubt");
    }

    for (const file of data.pdfs) {
      if (file.type !== "application/pdf") {
        errors.push("Nur PDF-Dateien sind erlaubt");
        break;
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push(
          `Datei "${file.name}" ist zu groß (max. 10 MB pro PDF)`
        );
        break;
      }
    }
  }

  if (!data.message || data.message.trim().length < 20) {
    errors.push("Dein Anschreiben muss mindestens 20 Zeichen lang sein");
  }

  if (data.message && data.message.length > 1000) {
    errors.push("Dein Anschreiben darf maximal 1000 Zeichen lang sein");
  }

  return errors;
}

export default function Jobs() {
  const [formData, setFormData] = useState<JobApplicationFormData>({
    name: "",
    email: "",
    role: "",
    showreelUrl: "",
    message: "",
    pdfs: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Don't update state if user cancelled the file dialog
    if (files.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      pdfs: files,
    }));
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      pdfs: prev.pdfs.filter((_, i) => i !== index),
    }));
    // Reset the file input so the displayed text matches the actual state
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Validation
      const errors = validateJobApplication(formData);
      if (errors.length > 0) {
        setSubmitMessage({
          type: "error",
          message: errors[0],
        });
        setIsSubmitting(false);
        return;
      }

      // Format payload for contact API using FormData (for file uploads)
      const formDataPayload = new FormData();
      formDataPayload.append("name", formData.name);
      formDataPayload.append("email", formData.email);
      formDataPayload.append("subject", `Bewerbung: ${formData.role}`);

      // Build message with showreel URL if provided
      let messageText = `Rolle: ${formData.role}\n\n`;
      if (formData.showreelUrl && formData.showreelUrl.trim().length > 0) {
        messageText += `Showreel: ${formData.showreelUrl}\n\n`;
      }
      messageText += formData.message;

      formDataPayload.append("message", messageText);
      formDataPayload.append("phone", "");

      // Append PDF files
      formData.pdfs.forEach((file) => {
        formDataPayload.append("pdfs", file);
      });

      const response = await fetch("/api/contact", {
        method: "POST",
        body: formDataPayload,
        // Note: Don't set Content-Type header - browser will set it with boundary
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Submission failed");
      }

      // Success
      setSubmitMessage({
        type: "success",
        message: "Bewerbung erhalten! Wir melden uns, wenn deine Küche bereit ist. 🔥",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        role: "",
        showreelUrl: "",
        message: "",
        pdfs: [],
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      setSubmitMessage({
        type: "error",
        message: "Etwas ist schiefgelaufen. Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative w-full flex items-start justify-center pt-24 pb-6 px-6 min-h-dvh md:h-dvh md:overflow-y-auto"
      style={{
        backgroundColor: "#101216",
      }}
    >
      <div className="w-full max-w-2xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-6"
        >
          {/* Fire Emoji */}
          <div className="text-5xl md:text-6xl mb-3">🔥</div>

          {/* Main Headline */}
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-2"
            style={{
              textShadow:
                "0 0 30px rgba(173, 174, 167, 0.6), 0 0 60px rgba(173, 174, 167, 0.3)",
            }}
          >
            Let Him Cook
          </h2>

          {/* Subheadline */}
          <p className="text-base md:text-lg text-white/70 font-light mb-4">
            Lust, auf der „Let Him Cook"-Seite zu stehen? Zeig uns, was du
            kannst.
          </p>

          {/* Supporting Text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xs md:text-sm text-white/60 max-w-lg mx-auto mb-4"
          >
            Keine Abschlüsse. Nur Skills. Wir suchen kreative Köpfe in allen
            Bereichen – von Beleuchter bis KI-Prompter. Du lieferst Showreel +
            kurzes Anschreiben. Wir geben dir die Küche.
          </motion.p>

          {/* Role Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-1.5 mb-6"
          >
            {visualRoles.map((role) => (
              <span
                key={role}
                className="px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-[10px] md:text-xs"
              >
                {role}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* Form Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-md mx-auto"
        >
          <h3 className="text-lg md:text-xl text-white font-semibold text-center mb-4">
            Deine Zutaten
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Name Field */}
              <div className="space-y-1">
                <label className="text-sm text-white/70" htmlFor="job-name">
                  Name *
                </label>
                <input
                  id="job-name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                  placeholder="Dein Name"
                />
              </div>

              {/* Email Field */}
              <div className="space-y-1">
                <label className="text-sm text-white/70" htmlFor="job-email">
                  E-Mail *
                </label>
                <input
                  id="job-email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                  placeholder="deine@email.de"
                />
              </div>
            </div>

            {/* Role Dropdown */}
            <div className="space-y-1">
              <label className="text-sm text-white/70" htmlFor="job-role">
                Rolle / Spezialgebiet *
              </label>
              <select
                id="job-role"
                name="role"
                required
                value={formData.role}
                onChange={handleInputChange}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
              >
                {roleOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-[#1a1f25] text-white"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Showreel URL (Optional) */}
            <div className="space-y-1">
              <label className="text-sm text-white/70" htmlFor="job-showreel">
                Showreel URL{" "}
                <span className="text-white/40 text-xs">(optional)</span>
              </label>
              <input
                id="job-showreel"
                name="showreelUrl"
                type="url"
                value={formData.showreelUrl}
                onChange={handleInputChange}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                placeholder="https://..."
              />
            </div>

            {/* PDF Upload for Arbeitsproben (Optional) */}
            <div className="space-y-1">
              <label className="text-sm text-white/70" htmlFor="job-pdfs">
                Arbeitsproben als PDF{" "}
                <span className="text-white/40 text-xs">
                  (optional, max. 3 Dateien à 10 MB)
                </span>
              </label>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  id="job-pdfs"
                  name="pdfs"
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white/40 flex items-center gap-3">
                  <span className="py-2 px-4 rounded-md bg-white/10 text-white/90 text-sm whitespace-nowrap">
                    Dateien auswählen
                  </span>
                  <span>
                    {formData.pdfs.length === 0
                      ? "Keine Datei ausgewählt"
                      : `${formData.pdfs.length} Datei${formData.pdfs.length > 1 ? "en" : ""} ausgewählt`}
                  </span>
                </div>
              </div>

              {/* Selected Files List */}
              {formData.pdfs.length > 0 && (
                <div className="mt-2 space-y-2">
                  {formData.pdfs.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-white/60 text-lg">📄</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-white/40">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-2 text-red-400 hover:text-red-300 transition-colors p-1"
                        aria-label={`Remove ${file.name}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message Textarea */}
            <div className="space-y-1">
              <label className="text-sm text-white/70" htmlFor="job-message">
                Kurzes Anschreiben *
              </label>
              <textarea
                id="job-message"
                name="message"
                required
                rows={3}
                value={formData.message}
                onChange={handleInputChange}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all resize-none"
                placeholder="Wer bist du? Was machst du? Warum brennst du dafür?"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-lg font-semibold transition-all ${
                isSubmitting
                  ? "bg-white/50 text-black/50 cursor-not-allowed"
                  : "bg-white text-black hover:bg-white/90 hover:scale-[1.02]"
              }`}
            >
              {isSubmitting ? "Senden..." : "Bewerbung abschicken"}
            </button>

            {/* Success/Error Message */}
            {submitMessage && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg text-sm text-center ${
                  submitMessage.type === "success"
                    ? "bg-green-500/20 text-green-300 border border-green-500/30"
                    : "bg-red-500/20 text-red-300 border border-red-500/30"
                }`}
              >
                {submitMessage.message}
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
